/**
 * Exported constants representing prompts for various user input fields.
 * @type {string}
 *
 * Modify these strings to change the prompts for the user input fields in the AudioRecorder class
 */

import { dbgLog } from "../utils/appDebugFlags";

export let supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
export let supabaseKey = process.env.REACT_APP_SUPABASE_KEY;
export let supabaseServiceKey = process.env.REACT_APP_SUPABASE_SERVICE_KEY;

// Store WTF_FORMS_URL loaded from backend config
export let wtfFormsUrl = process.env.REACT_APP_WTF_FORMS_URL || "";

// RDS base URL, from the backend's RDS_API_URL (one variable for both sides).
export let rdsApiUrl = "";

export const getBackendURL = () => {
  const hostname = window.location.hostname;
  return hostname === "localhost"
    ? BASE_URLS.local.backend
    : BASE_URLS.render.backend;
};

/**
 * Pull runtime configuration from the backend. THROWS on failure, deliberately.
 *
 * This call decides which Supabase project the browser signs in to. It used to
 * swallow its own errors and leave the build-time REACT_APP_* values in place,
 * which produces the worst kind of failure: the app looks fine, direct Supabase
 * queries work, and only the backend's JWT-gated endpoints return 401
 * "unauthenticated" — because the browser holds a token minted by one project
 * and the server verifies it against another. Reading that backwards costs
 * hours. Failing here costs a sentence.
 */
export const loadConfig = async () => {
  const backendUrl = getBackendURL();
  const endpoint = `${backendUrl}/api/config/`;

  let config;
  try {
    const response = await fetch(endpoint);
    if (!response.ok) {
      throw new Error(`${endpoint} returned HTTP ${response.status}`);
    }
    config = await response.json();
  } catch (error) {
    console.error("Error loading configuration:", error);
    throw new Error(
      `The backend could not be reached at ${endpoint}. ` +
        `Start it, or correct the backend URL, then reload. (${error.message})`
    );
  }

  // Reaching the backend is not the same as it being configured: blank values
  // here are exactly the case that used to fall through to build-time creds.
  if (!config.supabaseUrl || !config.supabaseKey) {
    throw new Error(
      `The backend at ${endpoint} is not configured for Supabase — it returned ` +
        `${config.supabaseUrl ? "" : "no supabaseUrl"}` +
        `${!config.supabaseUrl && !config.supabaseKey ? " and " : ""}` +
        `${config.supabaseKey ? "" : "no supabaseKey"}. ` +
        `Check SUPABASE_URL and SUPABASE_KEY in the backend environment.`
    );
  }

  supabaseUrl = config.supabaseUrl;
  supabaseKey = config.supabaseKey;
  if (config.supabaseServiceKey) supabaseServiceKey = config.supabaseServiceKey;
  if (config.wtfFormsUrl) wtfFormsUrl = config.wtfFormsUrl;
  rdsApiUrl = String(config.rdsApiUrl || "").replace(/\/$/, "");
};

// Dropbox Fax Number - The number patients should send faxes to
export const DROPBOX_FAX_NUMBER =
  process.env.REACT_APP_DROPBOX_FAX_NUMBER || "+1 213-282-6586";

/**
 * Vosk browser STT service origin. Mirrors the prior `process.env.REACT_APP_VOSK_SERVICE_ORIGIN`
 * config but lives as a hard-coded JS constant so edits take effect on the
 * next refresh — no CRA dev server restart required.
 */
// MUST be https — an http:// origin is blocked as mixed content on the HTTPS
// app (app.magicnotesai.com), which made the manifest fetch fail and forced the
// broken /vendors/vosk.js fallback. The tests already assert https.
export const VOSK_SERVICE_ORIGIN = "https://vosk-api-iota.vercel.app";

// User Roles and Permissions (Updated to enforce stricter access rules)
export const userRoles = {
  ADMIN: {
    accessPages: ["AdminPage"],
    description:
      "Can only access the Admin Page for managing patients and reports",
  },
  DOCTOR: {
    accessPages: [
      "AdminPage",
      "DoctorDashboard",
      "Reports",
      "Messaging",
      "UserHome",
      "Forms",
      "Messages",
      "Staff",
      "RecordAudios",
      "RecentAudios",
    ],
    description: "Medical professional with access to all pages",
  },
  USERS: {
    accessPages: ["RecordAudios", "RecentAudios"],
    description:
      "General users with access only to record and view recent audios",
  },
  // Tenant schema "other" (MISC) — same app access as student/socialworker
  OTHER: {
    accessPages: [
      "Schedules",
      "PatientPage",
      "Document",
      "Visit",
      "Patient",
      "EditDocument",
      "Search",
      "Terms",
      "Settings",
    ],
    description:
      "Other/MISC schema user with access to schedules and related pages",
  },
  // Med student — doctor-parity for page access plus the new clinic workspace
  MEDSTUDENT: {
    accessPages: [
      "AdminPage",
      "DoctorDashboard",
      "Reports",
      "Messaging",
      "UserHome",
      "Forms",
      "Messages",
      "Staff",
      "RecordAudios",
      "RecentAudios",
      "Schedules",
      "PatientPage",
      "Document",
      "Visit",
      "Patient",
      "EditDocument",
      "Clinic",
      "Search",
      "Terms",
      "Settings",
    ],
    description:
      "Medical student with doctor-parity access plus the clinic workspace",
  },
};
// Lowercase aliases so userRoles[<lowercase role from DB>] works
userRoles.other = userRoles.OTHER;
userRoles.medstudent = userRoles.MEDSTUDENT;

// Color scheme
export const royal_blue = "#4169E1";
export const navy_blue = "#000080";
export const turquoise = "#40E0D0";
export const template_blue = "#0000ff"; // Blue color used in NP/NS templates

export const filled_button_form = "#3182CE";
export const empty_button_form = turquoise;

export const live_transcript_pass = "corti2024";

/**
 * External recording: show "Run AI on live transcript" for clinical tabs.
 * Set REACT_APP_ENABLE_LIVE_CLINICAL_LLM=false to disable. LLM API keys stay on the server (CLAUDE_KEY); the client only calls /api/generate-ai-recap/.
 */
export const liveClinicalLlmEnabled =
  process.env.REACT_APP_ENABLE_LIVE_CLINICAL_LLM !== "false";

/**
 * Auto-copilot: automatically enable LLM-generated clinical tabs (A&P, Consider, Scores, Report).
 * Set to false to disable automatic tab generation. When false, these tabs are only shown
 * when copilotEnabled is explicitly set to true.
 */
export const auto_copilot = true;

/** Defaults for `schema_settings.audio_periodic_backup` when unset or invalid (external recorder). */
export const DEFAULT_AUDIO_PERIODIC_BACKUP = {
  keepCount: 1,
  intervalSeconds: 20,
  intervalMs: 20000,
};

// AI Assistant settings password - used to protect access to AI settings
export const SETTINGS_PASSWORD = "admin123"; // Change this to your desired password

// LLM User Token Limit - maximum tokens allowed per user
export const LLM_USER_TOKEN_LIMIT = 1000000;

export const data_column = "reports";

// ==================== DATABASE TABLE NAMES ====================
// Main patient records table - stores all patient information, appointments, and medical data
export const tableName = "notes-c";

// No-show appointments table - tracks patients who didn't show up for appointments
export const noShowTableName = "no_show";

// Duplicate records table - stores duplicate patient entries for cleanup
export const dupsTableName = "notes_dev_duplicate";

// System logs table - tracks application events, errors, and user actions
export const logsTableName = "logs_notes";

// No-show logs table - tracks logging events related to no-show appointments
export const noShowsLogsTableName = "no_show_logs";

// ==================== STORAGE BUCKET NAMES ====================
// Audio recordings bucket - stores patient audio files and recordings
export const bucketName = "audio";

// Audio backup bucket - stores backup copies of audio files for data protection
export const backupBucketName = "backup-audio";

// Document backup bucket - stores automatic backups of medical documents
export const documentBackupBucketName = "document-backups";

// Document backup metadata table - tracks backup information and timestamps
export const documentBackupTableName = "document_backups";

// Document files bucket - stores medical documents, templates, and patient files
export const bucketName_files = "files";

/**
 * Max size (megabytes) for JSON loaded from `files` / `document-backups` in the browser
 * (preflight + parse). Override with REACT_APP_FRONTEND_STORAGE_JSON_MAX_MB; defaults to 10 if unset/invalid.
 */
const _frontendStorageJsonMaxMbRaw =
  process.env.REACT_APP_FRONTEND_STORAGE_JSON_MAX_MB;
const _frontendStorageJsonMaxMbParsed = Number(_frontendStorageJsonMaxMbRaw);
export const FRONTEND_STORAGE_JSON_MAX_MB =
  _frontendStorageJsonMaxMbRaw != null &&
  String(_frontendStorageJsonMaxMbRaw).trim() !== "" &&
  Number.isFinite(_frontendStorageJsonMaxMbParsed) &&
  _frontendStorageJsonMaxMbParsed > 0
    ? _frontendStorageJsonMaxMbParsed
    : 10;

// Minutes until alert (recording time)
export const recordTimeAlert = 30;
// Range of now in minutes
export const rangeInMinutes = 15;
// Days until delete dups and no-shows from schedule table (PST)
export const resetTime = 15;
// Schedule refresh timer (minutes)
export const refreshTimer = 5;
// Pagination default amount of items per page
export const defaultItemsPerPage = 25;

export const SMS_STATUS_BEFORE_GRACE_PERIOD_DAYS = 7;
// Total Twilio send attempts for one failed SMS (original + retries) before it
// is left as "failed". Mirrored on the backend (which runs the retry worker) in
// backend/twilio_service/twilio_sms.py -> SMS_MAX_SEND_ATTEMPTS; override there
// with the SMS_MAX_SEND_ATTEMPTS env var. Change both together.
export const SMS_MAX_SEND_ATTEMPTS = 2;
// Minutes before a failed SMS is retried. Mirrored in twilio_sms.py ->
// SMS_RETRY_DELAY_MINUTES (env var of the same name). Permanent Twilio errors
// (STOP, invalid/landline number, bad credentials) are never retried.
export const SMS_RETRY_DELAY_MINUTES = 15;
export const by_appointment_date_method = false; // true = omars function false = appointment_date column

// How many days before/after the appointment date to fetch messages in PatientMessagingModal
export const FETCH_PERIOD_DAYS = 30;

export const file_reference_column = "reports";

export const num_levels_JSON = 5;

// ==================== VERSION HISTORY UI (VersionHistoryModal) ====================
// Rules governing automatic version creation and end-of-day consolidation.
// All thresholds are hardcoded here — change these constants to tune behaviour.
//
// Rule 1 — Substantial edit trigger:
//   300+ cumulative chars typed/deleted since last auto version → create version
// Rule 2 — Inactivity trigger:
//   10 min of no typing with any unsaved changes → create version
// Rule 3 — Minimum gap:
//   Never two auto versions within 30 min of each other
// Rule 4 — Daily cap + rolling merge:
//   Max 8 auto versions/day; when cap hit, drop the closest adjacent auto pair
//   and create room for a new one. Manual versions are exempt from cap and merge.
// Rule 5 — End-of-day thinning (midnight backend cron):
//   Auto versions thinned to 3 per day (first, last, biggest-diff-middle).
//   Manual versions are NEVER touched.

export const VERSIONING_CONFIG = {
  // Rule 1: cumulative characters changed (inserts + deletes) to trigger a version
  SUBSTANTIAL_CHANGE_CHARS: 300,
  // Rule 2: milliseconds of inactivity before a version is created (10 min)
  INACTIVITY_TIMEOUT_MS: 10 * 60 * 1000,
  // Rule 3: minimum milliseconds between two consecutive auto versions (30 min)
  MIN_GAP_BETWEEN_VERSIONS_MS: 10 * 60 * 1000,
  // Rule 4: maximum auto versions allowed per document per calendar day
  DAILY_AUTO_VERSION_CAP: 8,
  // Rule 5: how many auto versions to keep after end-of-day thinning
  END_OF_DAY_VERSIONS_TO_KEEP: 3,
};

// Helpers used by VersionHistoryModal for display-layer logic
export const getDailyVersionLimit = () =>
  VERSIONING_CONFIG.DAILY_AUTO_VERSION_CAP;
export const getEndOfDayVersionLimit = () =>
  VERSIONING_CONFIG.END_OF_DAY_VERSIONS_TO_KEEP;
export const isUserBasedVersioningEnabled = () => true;
export const getMaxUsersForLimit = () => 5;
export const getSingleUserExtraVersions = () => 0;
export const basename = "/NotesAI";

export const submissions_arrayUrls = "submissions_array";

export const singleLinkSubmissionUrl = "submission_url";

// Dynamic prompts based on tableName
let prompts;
if (tableName === "notes_dev_clone") {
  prompts = [
    ["name", "Name", "Required", ""],
    ["email", "Email", "Required", ""],
    ["tel_num", "Phone Number", "Optional", ""],
    ["dob", "DOB", "Optional", ""],
    ["format", "Output Format", "Optional", ""],
    ["type", "Type", "Required", "FFP"],
    ["submissions_array", "Submissions", "Optional", ""],
  ];
} else {
  // Default prompts for "notes-c" table
  prompts = [
    ["patient_name", "patient_name", "Required", ""],
    ["email", "Email", "Optional", ""],
    // tel_num is the CELL number — every SMS (welcome, reminders,
    // confirmations) goes to it. home_phone is contact info only.
    ["tel_num", "Cell Phone", "Optional", ""],
    ["home_phone", "Home Phone", "Optional", ""],
    ["dob", "dob", "Required", ""],
    ["type", "type", "Required", ""],
    ["EHR_ID", "EHR_ID", "Required", ""],
    ["physician_name", "physician_name", "Optional", "Dr Pallickal"],
  ];
}

export { prompts };

// General helper function to find field index by field name
export const getFieldIndex = (fieldName) => {
  const index = prompts.findIndex((prompt) => prompt[0] === fieldName);
  return index >= 0 ? index : null;
};

// Helper functions to get specific field indices (for backward compatibility)
export const getTypeFieldIndex = () => {
  return getFieldIndex("type");
};

export const getFormatFieldIndex = () => {
  return getFieldIndex("format");
};

export const getSubmissionUrlFieldIndex = () => {
  // Try different possible field names for submission URL
  const submissionFieldNames = [
    "submission_url",
    "submissions_array",
    "submissions",
  ];
  for (const fieldName of submissionFieldNames) {
    const index = getFieldIndex(fieldName);
    if (index !== null) {
      return index;
    }
  }
  return null;
};

// Display order configuration
export const displayOrder = {
  default: Array.from({ length: prompts.length }, (_, i) => i),
  compact: [0, 1], // Customize these indices for compact view
};

// Currently active display order - change this to use different configurations
export const activeDisplayOrder = displayOrder.default;

// Unique identifier col name
export const colid = "email";
export const colid2 = prompts[0][0]; // Folder name is colid2_colid -> subfolder for date_

// Function to generate document title based on provided format
export const generateDocFilename = (userInfo) => {
  const time = userInfo["time_"] || "";
  const name = userInfo[prompts[0][0]] || "";
  // Use EHR_ID field for document titles instead of colid (email)
  const id = userInfo["EHR_ID"] || userInfo[colid] || "";
  const typeIndex = getTypeFieldIndex();
  const type = userInfo[prompts[typeIndex] ? prompts[typeIndex][0] : ""] || "";
  // console.log("Generating filename with:", { time, name, id, type, userInfo }); // Debug log - disabled to reduce console spam

  // Format: 9:30 Last, First - 123456 FFP
  // Remove seconds from time if present (e.g., "9:30:00" -> "9:30")
  const formattedTime = time.includes(":")
    ? time.split(":").slice(0, 2).join(":")
    : time;

  return `${formattedTime} ${name} - ${id} ${type}`.trim();
};

// Column containing link to their patient page
export const linkColid = prompts[0][0];
// Use submissionUtils.js to handle submissions - this is a virtual column
// that gets data from both submissions_array and submission_url via extractAllSubmissionLinks
export const linkURL = "submissions";
// ... other environment variables

export const formCondition = "form_ind";

export const defaultFolderNameColumns = prompts
  .slice(0, 2)
  .map((prompt) => prompt[0]);
export const allowedFileTypes = [
  "pdf",
  "csv",
  "txt",
  "html",
  "jpg",
  "jpeg",
  "png",
];

export const defaultFolderName = `${prompts[0][0]} - ${prompts[1][0]}`;
export const promptUserInput = () => {
  const returnVars = [];
  for (let i = 0; i < prompts.length; i++) {
    let userInput = "";
    do {
      userInput = prompt(`${prompts[i][0]} (${prompts[i][1]})`, prompts[i][2]);
    } while (prompts[i][1] === "Required" && !userInput);
    returnVars.push(userInput);
  }

  const concatenatedInput = `${returnVars[2]} - ${returnVars[3]}`; // Change this for changing the name of the folder
  sessionStorage.setItem("concatenatedInput", concatenatedInput);
  return returnVars;
};

export const getConcatenatedInput = () => {
  const concatenatedInput = sessionStorage.getItem("concatenatedInput");
  return concatenatedInput;
};

// Images are found in the assets folder in the src folder
export const CONFIG_LOGO_1 = {
  showLogo1: true, // Set to true to display the Yoda Labs logo, false to hide it.
};

// Support both require() return shapes: { default: url } or url string
const logoReq = require("../assets/VitaMed_Logo.jpg");
export const logo = CONFIG_LOGO_1.showLogo1
  ? logoReq.default != null
    ? logoReq.default
    : logoReq
  : null;

export const CONFIG_LOGO_2 = {
  showLogo2: true, // Set to true to display the Donate button, false to hide it.
};

export const donate = CONFIG_LOGO_2.showLogo2
  ? require("../assets/Donate.png")
  : null;

export const navConfig = {
  showUploadJson: true, // Set to true to display the Upload JSON button, false to hide it.
};
export const navSearch = {
  showSearch: true, // Set to true to display the Search button, false to hide it.
};

export const navRecentAudios = {
  showReacentAudios: true, // Set to true to display the Recent Audios button, false to hide it.
};
export const navSignIn = {
  showSignIn: true, // Set to true to display the Sign In button, false to hide it.
};

export const navFileUpload = {
  showFileUpload: true, // Set to true to display the Sign In button, false to hide it.
};

// Admin password for doctor access
export const ADMIN_PASSWORD = "Admin123!";

// Corti repository path
export const cortiRepoPath = "C:\\Users\\naras\\Corti";

export const TEXT_CONTENT = {
  showHeader: true,
  header: "Magic Notes",
  showIntro: true,
  intro:
    "Introducing Magic Notes: the ultimate tool for capturing, transcribing, and transforming lectures into stunning notes within your preferred note-taking app like GoodNotes, Notability, or Notion. Organize your thoughts, ideas, and tasks effortlessly with its user-friendly interface and robust features.",
  showBody: true,
  body: "Whether you're a student, professional, or creative individual, Magic Notes is designed to streamline your note-taking process and enhance your productivity.",
  showFooter: true,
  footer:
    "For any issues with the app or comments contact support@magicnotes.ai.",
};

/* --------------------------- SCHEDULE VARIABLES --------------------------- */
// Default date is tomorrow for the schedules table
export const getDefaultDateString = () => {
  const additionalDaysFromToday = 0; // Change this for additional days from today

  const defaultDate = new Date(new Date() - 7 * 60 * 60000);
  return defaultDate.toISOString().split("T")[0];
};

/* ---------------------------- HOSTING VARIABLES --------------------------- */
export const BASE_URLS = {
  local: {
    http: "http://localhost:3000",
    ws: "ws://localhost:8001",
    backend: "http://localhost:8001",
    agenticai: "https://diegotest.vercel.app",
  },
  render: {
    http:
      typeof window !== "undefined"
        ? window.location.origin
        : process.env.REACT_APP_BACKEND_URL ||
          process.env.REACT_APP_HTTP_URL ||
          "https://notesai-c.onrender.com",
    ws:
      typeof window !== "undefined"
        ? `wss://${window.location.host}`
        : process.env.REACT_APP_WS_URL || "wss://notesai-c.onrender.com",
    // Honor an explicit backend override in the browser too. Without this, a
    // build whose origin ISN'T the backend (prod frontend served separately
    // from the Django API) sends /api/* and /twilio/* calls to its own origin,
    // which returns HTML — breaking response.json() with "Unexpected token '<'".
    // The SMS path (PatientMessagingModal.getBackendBaseUrl) already does this,
    // which is why SMS worked while form send/preview/generation did not.
    backend:
      typeof window !== "undefined"
        ? process.env.REACT_APP_BACKEND_URL &&
          process.env.REACT_APP_BACKEND_URL.trim() !== ""
          ? process.env.REACT_APP_BACKEND_URL
          : window.location.origin
        : process.env.REACT_APP_BACKEND_URL || "https://notesai-c.onrender.com",
    agenticai:
      process.env.REACT_APP_AGENTICAI_URL || "https://diegotest.vercel.app",
  },
};

/* --------------------------- UPGRADE / PAYWALL --------------------------- */
// Global defaults for the "Upgrade to Pro" button on the Schedules page.
// Per-schema overrides live in schema_settings.upgrade_enabled / upgrade_link
// (see sql/migrations/add_upgrade_settings.sql) and take precedence when set.
export const UPGRADE_DEFAULTS = {
  enabled: process.env.REACT_APP_UPGRADE_ENABLED === "true",
  link:
    process.env.REACT_APP_UPGRADE_LINK ||
    "https://buy.stripe.com/4gM28raocf0Og3ZeOOffy05",
};

export const getRedirectURL = () => {
  const baseURL =
    window.location.hostname === "localhost"
      ? BASE_URLS.local.http
      : window.location.origin;
  return `${baseURL}/NotesAI/auth/callback`;
};

export const getSendFormsUrl = () => {
  const hostname = window.location.hostname;
  const baseUrl =
    hostname === "localhost"
      ? BASE_URLS.local.backend
      : BASE_URLS.render.backend;
  const sendFormsUrl = `${baseUrl}/twilio/send-forms/`;
  dbgLog("env", "URL Construction:", {
    baseUrl,
    sendFormsUrl,
    environment: hostname,
  });
  return sendFormsUrl;
};

// WebSocket configuration
export const getWebSocketURL = () => {
  const hostname = window.location.hostname;
  const useNewServer = true; // Toggle for testing new Django Channels server

  if (hostname === "localhost") {
    if (useNewServer) {
      dbgLog("env", "Using new Django Channels WebSocket server");
      return `${BASE_URLS.local.ws}/collaboration`;
    }
    dbgLog("env", "Using old WebSocket server");
    return "ws://localhost:1234"; // Old server
  }

  dbgLog(
    "env",
    "Selected WebSocket URL:",
    `${BASE_URLS.render.ws}/collaboration`,
  );
  return `${BASE_URLS.render.ws}/collaboration`;
};

// User access control
export const checkUserAccess = (userRole, currentPage, navigate) => {
  if (!userRole || !userRoles[userRole]) {
    console.error("Invalid or missing user role:", userRole);
    navigate(`${basename}/login`); // Redirect to login if role is invalid
    return false;
  }

  const allowedPages = userRoles[userRole].accessPages;

  // Check if the current page is allowed for the user's role
  if (!allowedPages.includes(currentPage)) {
    console.warn(
      `Access denied: ${userRole} cannot access ${currentPage}. Redirecting to forbidden page...`,
    );
    navigate(`${basename}/forbidden`);
    return false;
  }

  dbgLog("env", `Access granted: ${userRole} can access ${currentPage}`);
  return true;
};

// ==================== WTF_FORMS CONFIGURATION ====================

/** Patient-facing forms (NP/NS, etc.) always use this host for links. */
const WTF_FORMS_PUBLIC_BASE_URL = "https://wtf-prod-drab.vercel.app";

// Helper function to get current WTF_forms configuration based on environment
export const getWTFFormsConfig = () => {
  const isLocalDev =
    typeof window !== "undefined" &&
    (window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1");

  // Local dev: always use Vercel for form URLs (ignore backend/env pointing at localhost:5000).
  // Else: wtfFormsUrl from backend, then env, then Vercel.
  const wtfFormsBaseUrl = isLocalDev
    ? WTF_FORMS_PUBLIC_BASE_URL
    : wtfFormsUrl ||
      process.env.REACT_APP_WTF_FORMS_URL ||
      process.env.WTF_FORMS_URL ||
      WTF_FORMS_PUBLIC_BASE_URL;

  return {
    baseURL: wtfFormsBaseUrl,
    apiEndpoint: wtfFormsBaseUrl ? `${wtfFormsBaseUrl}/api` : "",
    formsPath: "",
    submissionsPath: "/submissions",
    uploadPath: "/upload",
  };
};

// Environment-specific form URLs - now uses WTF_FORMS_URL from backend config
export const FORM_URLS = {
  development: {
    NP_FORM_URL:
      "https://wtf-prod-drab.vercel.app/bb76a841-4239-4e14-a0e7-3585cab4f78c",
    NS_FORM_URL:
      "https://wtf-prod-drab.vercel.app/991de856-cc1e-4d99-8584-22f5a61053c8",
  },
  production: {
    NP_FORM_URL:
      "https://wtf-prod-drab.vercel.app/bb76a841-4239-4e14-a0e7-3585cab4f78c",
    NS_FORM_URL:
      "https://wtf-prod-drab.vercel.app/991de856-cc1e-4d99-8584-22f5a61053c8",
  },
};

// Helper function to get form URLs based on environment
export const getFormUrls = () => {
  const hostname = window.location.hostname;

  if (hostname === "localhost" || hostname === "127.0.0.1") {
    return FORM_URLS.development;
  }

  // Default to production for other environments
  return FORM_URLS.production;
};

// Helper function to get WTF_forms base URL
export const getWTFFormsBaseURL = () => {
  return getWTFFormsConfig().baseURL;
};

// Helper function to get WTF_forms API endpoint
export const getWTFFormsAPIEndpoint = () => {
  return getWTFFormsConfig().apiEndpoint;
};

// Form configuration with enhanced WTF_forms integration
export const FORM_CONFIG = [
  {
    type: "NP",
    label: "New Pulmonary Patient",
    formId: "bb76a841-4239-4e14-a0e7-3585cab4f78c",
    category: "pulmonary",
    description: "Comprehensive questionnaire for new pulmonary patients",
  },
  {
    type: "NS",
    label: "New Sleep Patient",
    formId: "991de856-cc1e-4d99-8584-22f5a61053c8",
    category: "sleep",
    description: "Sleep assessment questionnaire for new patients",
  },
  {
    type: "CL1",
    label: "Clinical Form 1",
    formId: "3dc81e20-d069-475b-a94e-76bd615c6e26",
    category: "clinical",
    description: "General clinical assessment form",
  },
  {
    type: "CL2",
    label: "Clinical Form 2",
    formId: "953b1782-45b1-4161-b90d-a4dad57f0e9a",
    category: "clinical",
    description: "Specialized clinical assessment form",
  },
];

// Helper function to get form URL with environment awareness
export const getFormUrl = (type, userSettings = {}) => {
  const form = FORM_CONFIG.find((f) => f.type === type);
  if (!form) return "";

  // Check if user has custom URL for this form type
  const userCustomUrl = userSettings[`${type}_FORM_URL`];
  if (userCustomUrl) return userCustomUrl;

  // Use environment-specific base URL with form ID
  const config = getWTFFormsConfig();
  return `${config.baseURL}${config.formsPath}/${form.formId}`;
};

// Helper function to get form submission URL
export const getFormSubmissionUrl = (formId) => {
  const config = getWTFFormsConfig();
  return `${config.baseURL}${config.submissionsPath}/${formId}`;
};

// Helper function to get form upload URL
export const getFormUploadUrl = () => {
  const config = getWTFFormsConfig();
  return `${config.baseURL}${config.uploadPath}`;
};

// WTF_forms API configuration
export const WTF_FORMS_API = {
  // Timeout settings for API requests
  timeout: 30000, // 30 seconds

  // Retry settings
  retryAttempts: 3,
  retryDelay: 1000, // 1 second

  // Headers to include with API requests
  defaultHeaders: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },

  // API endpoints
  endpoints: {
    forms: "/forms",
    submissions: "/submissions",
    upload: "/upload",
    validate: "/validate",
  },
};

// Helper function to construct full API endpoint URL
export const getWTFFormsApiUrl = (endpoint) => {
  const config = getWTFFormsConfig();
  const apiPath = WTF_FORMS_API.endpoints[endpoint] || endpoint;
  return `${config.apiEndpoint}${apiPath}`;
};

/**
 * Resolve the WebSocket base URL for the medstudent collab consumer. Same
 * host as the backend HTTP origin, ws:// in dev, wss:// in prod.
 */
export function getCollabWSURL() {
  if (typeof window === "undefined") return "";
  const isLocal =
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1";
  const proto = window.location.protocol === "https:" ? "wss" : "ws";
  // Daphne dev server is :8001; in prod it's same-origin via the platform.
  const host = isLocal
    ? `${window.location.hostname}:8001`
    : window.location.host;
  return `${proto}://${host}`;
}
